import React, { FC, ReactNode, useEffect } from 'react'

import styled from 'styled-components'
import { useForm } from 'react-hook-form'

import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'

import { DesktopTabletTrackSearch } from './Components/DesktopTabletTrackSearch'
import { MobileTrackSearch } from './Components/MobileTrackSearch'
import { HowToGetTrackUrl } from './Components/HowToGetTrackUrl'

const Container = styled.div`
  display: flex;
  width: 100%;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  position: relative;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    border: none;
    box-shadow: none;
    padding: 0;
  }
`

const Title = styled.span`
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize18};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
`

export type SearchedTrack = {
  id: string
  name: string
  artistName: string
  trackCoverUrl: string
  previewUrl: string
  trackDuration: string
  resourceSpotifyUrl?: string
  ownerSpotifyUrl?: string
}

export interface TrackSearchProps {
  platform: CampaignPlatformTypeEnum
  isLoading: boolean
  onItemClick: (trackId: string, item?: SearchedTrack) => void
  onPlayClick?: () => void
  tracks: Array<SearchedTrack>
  onSearch: (search: string) => void
  error?: ReactNode
  defaultSearchValue?: string | null
  onOutsideClick: (e: MouseEvent) => void
  bottomContent?: ReactNode
}

export interface SearchTrackFormData {
  searchInput: string
}

const getTitle = (platform: CampaignPlatformTypeEnum): string =>
  platform === CampaignPlatformTypeEnum.TIKTOK
    ? 'New TikTok Campaign'
    : 'New Spotify Campaign'

const getPlaceholder = (platform: CampaignPlatformTypeEnum): string =>
  platform === CampaignPlatformTypeEnum.TIKTOK
    ? 'Type track name or URL from TikTok'
    : 'Type track name or URL from Spotify'

export const TrackSearch: FC<TrackSearchProps> = ({
  platform,
  isLoading,
  onItemClick,
  onPlayClick,
  tracks = [],
  onSearch,
  error,
  defaultSearchValue,
  onOutsideClick,
  bottomContent,
}) => {
  const { control } = useForm<SearchTrackFormData>({
    mode: 'onChange',
  })

  useEffect(() => {
    if (defaultSearchValue) {
      onSearch(defaultSearchValue)
    }
  }, [defaultSearchValue, onSearch])

  const platformTitle = getTitle(platform)

  const placeholder = getPlaceholder(platform)

  return (
    <Container>
      <Title>{platformTitle}</Title>

      <DesktopTabletTrackSearch
        isLoading={isLoading}
        onItemClick={onItemClick}
        onPlayClick={onPlayClick}
        tracks={tracks}
        onSearch={onSearch}
        onOutsideClick={onOutsideClick}
        placeholder={placeholder}
        control={control}
        error={error}
        defaultSearchValue={defaultSearchValue}
      />

      <MobileTrackSearch
        isLoading={isLoading}
        onItemClick={onItemClick}
        onPlayClick={onPlayClick}
        tracks={tracks}
        onSearch={onSearch}
        placeholder={placeholder}
        control={control}
        error={error}
        defaultSearchValue={defaultSearchValue}
      />

      {bottomContent}

      <HowToGetTrackUrl platform={platform} />
    </Container>
  )
}
